import { Box, Chip, Stack, Typography, useTheme } from '@mui/material';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ChangeActivityItemContentFragment } from '@graphql/__generated__/graphql';
import {
  coerceCurrency,
  formatAutoReviewBreakdown,
  formatReasonCode,
  parseChangeExtraData,
} from '@modules/_shared/utils/lotChangeExtraData';
import { useLocale } from '@shared/hooks/locale';
import { formatPrice } from '@shared/utils/format-price';

export const CHANGE_ACTIVITY_ITEM_CONTENT_FRAGMENT = gql(/* GraphQL */ `
  fragment ChangeActivityItemContent on activities {
    id
    lot_id
    extra_data
  }
`);

type Props = {
  activity: ChangeActivityItemContentFragment;
};

export const ChangeActivityItemContent = ({ activity }: Props) => {
  const { t, locale } = useLocale();
  const theme = useTheme();

  // Defensive: change activities are only meaningful in a lot context.
  if (activity.lot_id == null) {
    return null;
  }

  const extra = parseChangeExtraData(activity.extra_data);
  // activity.note (internal note) is rendered separately by ActivityItem via
  // CollapsibleMarkdownContent — we intentionally don't surface it here.

  let priceDelta: React.ReactNode = null;
  if (extra && 'price' in extra) {
    const delta = extra.price.to - extra.price.from;
    if (delta !== 0) {
      const cur = coerceCurrency(extra.price.currency);
      const sign = delta > 0 ? '+' : '-';
      const pct =
        extra.price.from !== 0 ? (delta / extra.price.from) * 100 : null;
      const absDelta = formatPrice(Math.abs(delta), locale, cur);
      const pctText =
        pct != null ? ` (${sign}${Math.abs(pct).toFixed(1)}%)` : '';
      priceDelta = (
        <Chip
          label={`${sign}${absDelta}${pctText}`}
          size="small"
          color={delta > 0 ? 'success' : 'error'}
          variant="outlined"
        />
      );
    }
  }

  const fsbo = extra != null && 'fsbo' in extra ? extra.fsbo : undefined;
  // Require fsbo so agent-driven status flips don't get the deactivation tone.
  const isFsboDeactivation =
    extra != null &&
    'status' in extra &&
    'fsbo' in extra &&
    extra.status.to === 'paused';
  const reasonCode = fsbo?.reason_code;
  const sellerMessage =
    fsbo != null && 'seller_message' in fsbo ? fsbo.seller_message : undefined;
  const soldOrRentedOnRa =
    fsbo != null && 'sold_or_rented_on_realadvisor' in fsbo
      ? fsbo.sold_or_rented_on_realadvisor
      : undefined;

  const hasFeedback = sellerMessage != null && sellerMessage.trim() !== '';
  const hasRaMatchAnswer = soldOrRentedOnRa != null;

  // Auto-review variant: render the full per-signal breakdown as Label: value
  // rows (consistent with the other change-activity fields), so a reviewer
  // sees exactly what each check found and why a held listing fell short.
  const autoReview =
    extra != null && 'auto_review' in extra ? extra.auto_review : undefined;

  if (
    priceDelta == null &&
    reasonCode == null &&
    !hasFeedback &&
    !hasRaMatchAnswer &&
    autoReview == null
  ) {
    return null;
  }

  const reasonLabel = isFsboDeactivation
    ? t('Deactivation reason')
    : t('Rejection reason');
  const reasonChipColor: 'warning' | 'error' = isFsboDeactivation
    ? 'warning'
    : 'error';

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {priceDelta != null && <Box>{priceDelta}</Box>}

      {autoReview != null && (
        <Stack spacing={0.5}>
          {formatAutoReviewBreakdown(autoReview, t, locale).map(row => (
            <Stack
              key={row.label}
              direction="row"
              spacing={0.75}
              sx={{ alignItems: 'center' }}
            >
              {row.status === 'pass' ? (
                <CheckCircle2
                  size={14}
                  color={theme.palette.success.main}
                  style={{ flexShrink: 0 }}
                />
              ) : (
                <AlertTriangle
                  size={14}
                  color={theme.palette.error.main}
                  style={{ flexShrink: 0 }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 500 }}>
                  {row.label}:
                </Box>{' '}
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {reasonCode != null && (
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            alignItems: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {reasonLabel}:
          </Typography>
          <Chip
            label={formatReasonCode(reasonCode, t)}
            size="small"
            color={reasonChipColor}
            variant="outlined"
          />
        </Stack>
      )}
      {hasRaMatchAnswer && (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
          }}
        >
          <Box component="span" sx={{ fontWeight: 500 }}>
            {t('Found on RealAdvisor')}:
          </Box>{' '}
          {soldOrRentedOnRa ? t('Yes') : t('No')}
        </Typography>
      )}
      {hasFeedback && (
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {t('Feedback to user')}
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              bgcolor: 'grey.100',
              borderRadius: '6px',
              color: 'text.secondary',
              fontSize: '0.75rem',
              fontStyle: 'italic',
              whiteSpace: 'pre-wrap',
            }}
          >
            {sellerMessage}
          </Box>
        </Box>
      )}
    </Stack>
  );
};
